// ═══════════════════════════════════════════════════════════════════════
// benefitsData.js — 76 CLM benefits as structured data
// ═══════════════════════════════════════════════════════════════════════
// Source of truth: CLM_Benefits_Checklist.md
// 14 categories · monitored ✓ / felt ✗ · Phase (during / upon / both) ·
// audience (customer / adopter / both).
// ═══════════════════════════════════════════════════════════════════════

export const BENEFIT_CATEGORIES = [
  { id: "ENG",    label: "Engineering & Design",          icon: "🛠️",  color: "#00e5ff", scope: "PLM-anchored" },
  { id: "MFG",    label: "Manufacturing Execution",        icon: "⚙️",  color: "#fb923c", scope: "MES + Shop Floor" },
  { id: "FIN",    label: "Transactional & Finance",        icon: "💼",  color: "#ffd600", scope: "ERP-anchored" },
  { id: "QUAL",   label: "Quality & Compliance",           icon: "🔬",  color: "#ff4f8b", scope: "QMS / regulated" },
  { id: "SCM",    label: "Supply Chain & B2B",             icon: "🚚",  color: "#38a8ff", scope: "Partner integration" },
  { id: "WF",     label: "Workforce & Adoption",           icon: "👥",  color: "#c084fc", scope: "People & change" },
  { id: "DATA",   label: "Data, Analytics & Decisions",    icon: "📊",  color: "#00d4c0", scope: "Insight & ML" },
  { id: "IT",     label: "Integration & Architecture",     icon: "🔌",  color: "#64b5d6", scope: "IT backbone" },
  { id: "CX",     label: "Customer Experience",            icon: "🎯",  color: "#00ff88", scope: "Downstream value" },
  { id: "ESG",    label: "Sustainability & ESG",           icon: "🌱",  color: "#34d399", scope: "Carbon & circular" },
  { id: "RISK",   label: "Risk & Resilience",              icon: "🛡️",  color: "#ff4444", scope: "Recall, SPOF, cyber" },
  { id: "STRAT",  label: "Strategic & Competitive",        icon: "🏆",  color: "#ffb300", scope: "TTM, M&A, brand" },
  { id: "CULT",   label: "Cultural & Behavioral",          icon: "🤝",  color: "#a78bfa", scope: "Trust, decisions" },
  { id: "KM",     label: "Knowledge & Improvement",        icon: "📚",  color: "#5eead4", scope: "Lessons, runbooks" },
];

// Phase tags: "during" (visible while building), "upon" (steady-state),
// "both" (grows continuously)
// Audience tags: "customer" (sponsor lens), "adopter" (operator lens), "both"

export const BENEFITS = [
  // ─── 01 · Engineering & Design ───────────────────────────────────────
  { id: "B-01", cat: "ENG", name: "ECN cycle-time compression",
    meaning: "Time from ECN approval to that change being effective in MES and posted to ERP.",
    keywords: ["change management", "effectivity", "time-to-floor", "engineering velocity", "ECN", "AENNR"],
    verbs: ["release the change", "propagate the effectivity", "apply the revision", "close the loop"],
    monitored: true, metric: "hours, P50 and P99", target: "≤ 4h P50, ≤ 24h P99",
    phase: "both", audience: "both",
    rationale: "Engineering velocity = revenue speed (customer); engineer pain removed (adopter).",
    topMonitored: 1 },

  { id: "B-02", cat: "ENG", name: "BOM accuracy & single-source truth",
    meaning: "Engineering BOM, manufacturing BOM, and ERP STKO/STPO all reconcile to the same authoritative record.",
    keywords: ["BOM", "MBOM", "EBOM", "STKO", "master data", "golden record"],
    verbs: ["author the BOM", "reconcile the structure", "publish the version"],
    monitored: true, metric: "BOM reconciliation rate", target: "≥ 99.5% match",
    phase: "both", audience: "both",
    rationale: "Cost integrity (customer); planners stop fighting spreadsheets (adopter)." },

  { id: "B-03", cat: "ENG", name: "Engineering re-use rate",
    meaning: "Share of new designs that reuse existing released parts and subassemblies instead of creating duplicates.",
    keywords: ["re-use", "part rationalization", "design efficiency", "classification"],
    verbs: ["find the part", "reuse the assembly", "classify the family"],
    monitored: true, metric: "% new items that are reused", target: "≥ 35%",
    phase: "upon", audience: "both",
    rationale: "Lower part-count cost (customer); faster designs (adopter)." },

  { id: "B-04", cat: "ENG", name: "CAD-to-MBOM consistency",
    meaning: "Geometry, structure, and metadata stay aligned between CAD models, EBOM, and MES MBOMView with no manual re-keying.",
    keywords: ["CAD-PLM sync", "geometry", "metadata", "JT/STEP", "dataset"],
    verbs: ["link the dataset", "publish the geometry", "view the assembly"],
    monitored: true, metric: "orphan-CAD rate", target: "< 0.1% of released items",
    phase: "upon", audience: "adopter",
    rationale: "Engineers and process planners feel this most." },

  { id: "B-05", cat: "ENG", name: "Effectivity precision",
    meaning: "Changes apply to exactly the right serial numbers, dates, or order ranges — never too broadly, never missed.",
    keywords: ["effectivity", "serial-effective", "date-effective", "AENNR"],
    verbs: ["scope the effectivity", "gate the rollout", "cut over the revision"],
    monitored: true, metric: "wrong-effectivity NCR count", target: "0",
    phase: "upon", audience: "customer",
    rationale: "Cost of bad rollouts avoided." },

  { id: "B-06", cat: "ENG", name: "Engineering knowledge retention",
    meaning: "When a senior engineer leaves, the design rationale, approval chain, and history don't leave with them.",
    keywords: ["knowledge management", "audit trail", "decision history"],
    verbs: ["preserve the rationale", "audit the history"],
    monitored: false, metric: "felt only when someone tries to find old context",
    phase: "upon", audience: "both",
    rationale: "Continuity (customer); new hires have what they need (adopter).",
    topNonMonitored: 1 },

  { id: "B-07", cat: "ENG", name: "Design-to-manufacturing handoff quality",
    meaning: "The package that goes from engineering to manufacturing planning is complete, structured, and ready to execute.",
    keywords: ["design transfer", "NPI handoff", "manufacturing prep"],
    verbs: ["hand off the package", "approve the transfer"],
    monitored: true, metric: "rework requests from MES back to PLM", target: "< 5%",
    phase: "upon", audience: "adopter",
    rationale: "Process planners; cost of rework avoided (customer)." },

  // ─── 02 · Manufacturing Execution ────────────────────────────────────
  { id: "B-08", cat: "MFG", name: "OEE (Overall Equipment Effectiveness)",
    meaning: "Single composite score combining availability × performance × quality for every machine/line.",
    keywords: ["OEE", "availability", "performance", "quality", "lean"],
    verbs: ["score the equipment", "monitor the run", "attribute the loss"],
    monitored: true, metric: "% per shift, day, line", target: "≥ 82%",
    phase: "during", audience: "both",
    rationale: "Asset productivity (customer); plant manager's dashboard (adopter).",
    topMonitored: 2 },

  { id: "B-09", cat: "MFG", name: "FPY (First Pass Yield)",
    meaning: "Share of units that pass inspection on the first attempt without rework or scrap.",
    keywords: ["FPY", "rework", "scrap", "in-process quality"],
    verbs: ["yield the unit", "pass the inspection", "contain the defect"],
    monitored: true, metric: "% per part-family or line", target: "≥ 92%",
    phase: "both", audience: "both",
    topMonitored: 3 },

  { id: "B-10", cat: "MFG", name: "Real-time floor visibility",
    meaning: "Supervisors and plant managers see live state of every order, operation, and machine — not a 24h-old report.",
    keywords: ["live state", "dispatch", "Andon", "real-time"],
    verbs: ["see the floor", "dispatch the order", "respond to the alert"],
    monitored: true, metric: "dashboard freshness latency", target: "< 5s",
    phase: "during", audience: "adopter",
    rationale: "Reduced firefighting (customer)." },

  { id: "B-11", cat: "MFG", name: "Cycle-time variance reduction",
    meaning: "Operations finish in predictable windows; outliers are detected and explained, not absorbed.",
    keywords: ["cycle time", "variance", "takt"],
    verbs: ["time the operation", "explain the variance"],
    monitored: true, metric: "coefficient of variation per operation", target: "< 0.15",
    phase: "upon", audience: "both" },

  { id: "B-12", cat: "MFG", name: "Changeover time reduction",
    meaning: "Time from last good piece of part A to first good piece of part B — minimized with digital SMED and live tracking.",
    keywords: ["SMED", "changeover", "setup time", "agility"],
    verbs: ["change the setup", "qualify the first piece"],
    monitored: true, metric: "minutes per changeover", target: "-30% vs baseline",
    phase: "both", audience: "both" },

  { id: "B-13", cat: "MFG", name: "Schedule adherence",
    meaning: "% of orders completed in the planned shift/day vs schedule.",
    keywords: ["adherence", "plan attainment", "dispatch discipline"],
    verbs: ["attain the plan", "sequence the orders"],
    monitored: true, metric: "% schedule adherence", target: "≥ 90%",
    phase: "upon", audience: "both" },

  { id: "B-14", cat: "MFG", name: "Lot-level genealogy & traceability",
    meaning: "For any finished good, instantly reconstruct: every input lot, every operator, every machine, every measurement.",
    keywords: ["genealogy", "traceability", "CHARG", "lot", "serial"],
    verbs: ["trace the lot", "reconstruct the genealogy", "scope the recall"],
    monitored: true, metric: "trace-completeness %", target: "100% for regulated lines",
    phase: "upon", audience: "both" },

  { id: "B-15", cat: "MFG", name: "Andon response time",
    meaning: "From operator triggering an Andon (issue signal) to the right person actually acting on it.",
    keywords: ["Andon", "escalation", "response time"],
    verbs: ["raise the Andon", "acknowledge the call", "resolve the issue"],
    monitored: true, metric: "minutes", target: "< 5 min P50",
    phase: "during", audience: "adopter" },

  { id: "B-16", cat: "MFG", name: "Operator-paced operation confidence",
    meaning: "Operators trust the system enough to follow the WI, log the confirm, and report the issue.",
    keywords: ["trust", "adoption", "paperless floor", "work instructions"],
    verbs: ["follow the WI", "log the step", "trust the system"],
    monitored: false, metric: "felt as the absence of paper, the disappearance of shadow Excel",
    phase: "upon", audience: "adopter",
    topNonMonitored: 2 },

  // ─── 03 · Transactional & Finance ────────────────────────────────────
  { id: "B-17", cat: "FIN", name: "Production confirmation accuracy",
    meaning: "AFRU postings to SAP match what actually happened on the floor — without manual reconciliation.",
    keywords: ["AFRU", "confirmation", "posting accuracy", "cost"],
    verbs: ["confirm the order", "post the actuals", "reconcile the variance"],
    monitored: true, metric: "posting variance rate", target: "< 0.5%",
    phase: "upon", audience: "both" },

  { id: "B-18", cat: "FIN", name: "Inventory accuracy",
    meaning: "Book stock equals physical stock at the SKU/lot/location level, all the time.",
    keywords: ["inventory", "MSEG", "cycle count", "stock accuracy"],
    verbs: ["count the stock", "reconcile the balance", "adjust the variance"],
    monitored: true, metric: "inventory accuracy %", target: "≥ 99%",
    phase: "upon", audience: "both" },

  { id: "B-19", cat: "FIN", name: "Working capital release (inventory days)",
    meaning: "Days of inventory on hand falls because demand-supply signals are tight and trustworthy.",
    keywords: ["working capital", "DIO", "cash", "inventory days"],
    verbs: ["free the cash", "turn the inventory"],
    monitored: true, metric: "days of inventory", target: "-20% vs baseline",
    phase: "upon", audience: "customer",
    topMonitored: 4 },

  { id: "B-20", cat: "FIN", name: "Cost-of-goods accuracy & roll-up",
    meaning: "Standard vs actual cost variances are explainable in real time, not at month-end.",
    keywords: ["COGS", "standard cost", "variance", "costing"],
    verbs: ["roll up the cost", "explain the variance"],
    monitored: true, metric: "unexplained variance %", target: "< 2%",
    phase: "upon", audience: "customer" },

  { id: "B-21", cat: "FIN", name: "Material planning accuracy (MRP signal quality)",
    meaning: "MRP runs on clean BOMs, accurate stock, and real-time consumption — fewer nervous re-plans.",
    keywords: ["MRP", "planning", "demand-supply", "expediting"],
    verbs: ["plan the requirements", "stabilize the signal"],
    monitored: true, metric: "planning nervousness index", target: "< 5% weekly re-plans",
    phase: "upon", audience: "adopter" },

  { id: "B-22", cat: "FIN", name: "Period close speed",
    meaning: "Finance can close the period in days instead of weeks because production cost data is already current and clean.",
    keywords: ["period close", "financial close", "month-end"],
    verbs: ["close the books", "post the period"],
    monitored: true, metric: "days to close", target: "≤ 4 working days",
    phase: "upon", audience: "customer",
    topMonitored: 5 },

  // ─── 04 · Quality & Compliance ───────────────────────────────────────
  { id: "B-23", cat: "QUAL", name: "NCR closure cycle",
    meaning: "From defect detection to closed CAPA, with closed-loop feedback to PLM.",
    keywords: ["NCR", "CAPA", "QMEL", "defect", "disposition"],
    verbs: ["raise the NCR", "disposition the lot", "close the CAPA", "update the BOM"],
    monitored: true, metric: "days to close", target: "≤ 14 days P50",
    phase: "both", audience: "both" },

  { id: "B-24", cat: "QUAL", name: "In-process inspection coverage",
    meaning: "Characteristics defined in PLM flow as inspection plans to MES; results land back in PLM and ERP QM.",
    keywords: ["characteristic", "QPMK", "QALS", "inspection plan"],
    verbs: ["define the characteristic", "sample the lot", "record the result"],
    monitored: true, metric: "% required inspections completed", target: "100% for regulated",
    phase: "upon", audience: "customer" },

  { id: "B-25", cat: "QUAL", name: "Cost of poor quality (COPQ)",
    meaning: "Total cost of scrap, rework, returns, and warranty as a % of revenue.",
    keywords: ["COPQ", "scrap", "rework", "returns", "warranty"],
    verbs: ["contain the defect", "prevent the recurrence"],
    monitored: true, metric: "COPQ % of revenue", target: "-25% in 2 years",
    phase: "both", audience: "customer",
    topMonitored: 6 },

  { id: "B-26", cat: "QUAL", name: "Audit readiness & pass rate",
    meaning: "Internal, customer, and regulatory audits pass with zero critical findings — because the trail exists by design.",
    keywords: ["audit", "21 CFR Part 11", "GMP", "ISO", "traceability"],
    verbs: ["prepare the evidence", "walk the audit", "pass the inspection"],
    monitored: true, metric: "critical findings per audit", target: "0",
    phase: "upon", audience: "customer" },

  { id: "B-27", cat: "QUAL", name: "Closed-loop quality",
    meaning: "A defect detected on the floor automatically becomes an investigation, then potentially an ECN, then a design or process change.",
    keywords: ["closed loop", "NCR-to-ECN", "learning loop", "CAPA"],
    verbs: ["feed back the finding", "trigger the change", "close the loop"],
    monitored: true, metric: "% NCRs that triggered a permanent fix", target: "≥ 30%",
    phase: "upon", audience: "both" },

  // ─── 05 · Supply Chain & B2B ─────────────────────────────────────────
  { id: "B-28", cat: "SCM", name: "OTD (On-Time Delivery)",
    meaning: "% of customer orders delivered in full and on the requested date.",
    keywords: ["OTD", "OTIF", "delivery reliability", "customer"],
    verbs: ["deliver the order", "meet the date"],
    monitored: true, metric: "% OTD", target: "≥ 95%",
    phase: "upon", audience: "customer",
    topMonitored: 7 },

  { id: "B-29", cat: "SCM", name: "ASN accuracy & supplier collaboration",
    meaning: "Inbound shipments are pre-notified, machine-readable, and reconciled at receipt — no surprises at the dock.",
    keywords: ["ASN", "EDIFACT DESADV", "supplier integration"],
    verbs: ["receive the ASN", "reconcile the receipt"],
    monitored: true, metric: "ASN match rate", target: "≥ 98%",
    phase: "upon", audience: "both" },

  { id: "B-30", cat: "SCM", name: "Partner onboarding time",
    meaning: "Time to bring a new supplier, customer, or 3PL onto the B2B gateway.",
    keywords: ["onboarding", "partner directory", "AS2", "EDIFACT"],
    verbs: ["onboard the partner", "exchange the certs", "validate the mapping"],
    monitored: true, metric: "business days", target: "≤ 10 days for standard partner",
    phase: "upon", audience: "both",
    topMonitored: 8 },

  { id: "B-31", cat: "SCM", name: "EDI exception rate",
    meaning: "Inbound and outbound EDI messages that need manual intervention.",
    keywords: ["EDI exceptions", "B2B", "rejection rate"],
    verbs: ["exchange the document", "resolve the exception"],
    monitored: true, metric: "exception %", target: "< 1%",
    phase: "upon", audience: "adopter" },

  { id: "B-32", cat: "SCM", name: "Customer EDI compliance",
    meaning: "Mandatory EDI compliance scorecards from major customers — meeting them protects revenue.",
    keywords: ["compliance", "chargeback", "OEM", "retailer"],
    verbs: ["comply with the standard", "avoid the chargeback"],
    monitored: true, metric: "chargebacks per quarter", target: "0",
    phase: "upon", audience: "customer" },

  // ─── 06 · Workforce & Adoption ───────────────────────────────────────
  { id: "B-33", cat: "WF", name: "Operator training & ramp time",
    meaning: "Time for a new operator to reach full productive output on a station.",
    keywords: ["training", "ramp", "work instructions", "onboarding"],
    verbs: ["train the operator", "ramp the station", "qualify the role"],
    monitored: true, metric: "days/weeks", target: "-40% vs paper-WI baseline",
    phase: "upon", audience: "both" },

  { id: "B-34", cat: "WF", name: "Reduction in tribal-knowledge dependency",
    meaning: "The plant doesn't grind to a halt when the one person who knows X is on vacation or leaves.",
    keywords: ["bus factor", "tribal knowledge", "key-person risk"],
    verbs: ["capture the procedure", "document the rationale"],
    monitored: false, metric: "felt as resilience to absences",
    phase: "upon", audience: "both",
    topNonMonitored: 3 },

  { id: "B-35", cat: "WF", name: "Operator empowerment & engagement",
    meaning: "Operators have the tools, data, and authority to act on issues — not just escalate.",
    keywords: ["empowerment", "engagement", "autonomy"],
    verbs: ["empower the operator", "act at the edge"],
    monitored: false, metric: "observed in pulse surveys, retention, suggestion rate",
    phase: "upon", audience: "adopter",
    topNonMonitored: 4 },

  { id: "B-36", cat: "WF", name: "Skills coverage & cross-training",
    meaning: "Plant can see who is qualified for which station, which skills are scarce, where training is needed.",
    keywords: ["skills matrix", "qualification", "cross-training"],
    verbs: ["qualify the operator", "plan the cross-training"],
    monitored: true, metric: "qualification coverage % per station", target: "≥ 3 qualified per station",
    phase: "upon", audience: "adopter" },

  { id: "B-37", cat: "WF", name: "Engineer/planner productivity",
    meaning: "Engineers and planners spend more time on engineering and planning — less on chasing data and reconciling spreadsheets.",
    keywords: ["productivity", "time-on-task", "automation"],
    verbs: ["automate the task", "free the engineer"],
    monitored: false, metric: "partially via time-tracking studies",
    phase: "both", audience: "both",
    topNonMonitored: 5 },

  { id: "B-38", cat: "WF", name: "Reduction in shadow IT",
    meaning: "Personal spreadsheets, Access databases, and side-channel email chains that hold critical data quietly disappear.",
    keywords: ["shadow IT", "spreadsheet sprawl", "side systems"],
    verbs: ["retire the spreadsheet", "centralize the source"],
    monitored: false, metric: "partial (inventory of decommissioned tools)",
    phase: "both", audience: "both",
    topNonMonitored: 6 },

  // ─── 07 · Data, Analytics & Decisions ────────────────────────────────
  { id: "B-39", cat: "DATA", name: "KPI freshness & trust",
    meaning: "The number on the dashboard is current and trusted — meetings stop arguing about whose number is right.",
    keywords: ["data freshness", "KPI", "trust", "single source"],
    verbs: ["refresh the KPI", "trust the number"],
    monitored: true, metric: "data-freshness latency", target: "< 5s for ops KPIs",
    phase: "upon", audience: "both",
    topMonitored: 9 },

  { id: "B-40", cat: "DATA", name: "Cross-system data consistency",
    meaning: "The same business entity (material, BOM, order) has the same value everywhere.",
    keywords: ["MDM", "golden record", "reconciliation"],
    verbs: ["steward the master", "resolve the duplicate"],
    monitored: true, metric: "duplicate-master rate", target: "< 0.1%",
    phase: "upon", audience: "both" },

  { id: "B-41", cat: "DATA", name: "Anomaly detection (ML on telemetry)",
    meaning: "Subtle deviations in machine signals or quality data are caught before they become defects.",
    keywords: ["ML", "anomaly", "predictive", "telemetry"],
    verbs: ["detect the anomaly", "flag the drift"],
    monitored: true, metric: "true-positive rate of alerts", target: "≥ 70%",
    phase: "upon", audience: "both" },

  { id: "B-42", cat: "DATA", name: "Forecast & demand-planning accuracy",
    meaning: "Better signals from the mesh feed better forecasts — fewer expedites, fewer stockouts.",
    keywords: ["forecast accuracy", "MAPE", "planning"],
    verbs: ["forecast the demand", "improve the accuracy"],
    monitored: true, metric: "MAPE", target: "≤ 15% at SKU-month",
    phase: "upon", audience: "customer" },

  { id: "B-43", cat: "DATA", name: "Faster, evidence-based decisions",
    meaning: "Decisions in meetings happen now (with data on screen) instead of 'let me get back to you with the numbers.'",
    keywords: ["decision velocity", "evidence", "data culture"],
    verbs: ["decide in the meeting", "cite the data"],
    monitored: false, metric: "felt as meeting length, decision frequency",
    phase: "upon", audience: "both",
    topNonMonitored: 7 },

  // ─── 08 · Integration & Architecture ─────────────────────────────────
  { id: "B-44", cat: "IT", name: "Mean time to integrate a new plant or partner",
    meaning: "Once the framework exists, each new plant or partner takes days/weeks — not months.",
    keywords: ["integration velocity", "time-to-onboard", "template"],
    verbs: ["template the rollout", "clone the integration"],
    monitored: true, metric: "weeks per plant", target: "≤ 12 weeks for plant N+1",
    phase: "upon", audience: "customer",
    topMonitored: 10 },

  { id: "B-45", cat: "IT", name: "Backbone availability",
    meaning: "Integration bus, gateway, IdP, and DLQ infrastructure are up; flows keep flowing.",
    keywords: ["availability", "SLO", "uptime", "backbone"],
    verbs: ["meet the SLO", "prevent the outage"],
    monitored: true, metric: "% monthly", target: "≥ 99.9%",
    phase: "during", audience: "both",
    topMonitored: 11 },

  { id: "B-46", cat: "IT", name: "MTTR (Mean Time To Recover)",
    meaning: "When a flow breaks, the average time from detection to restoration.",
    keywords: ["MTTR", "incident", "recovery", "resilience"],
    verbs: ["detect the failure", "restore the flow"],
    monitored: true, metric: "minutes/hours", target: "≤ 30 min for P1",
    phase: "upon", audience: "both",
    topMonitored: 12 },

  { id: "B-47", cat: "IT", name: "Reduction in integration unit cost",
    meaning: "Cost per new interface drops sharply once the delivery framework is in place.",
    keywords: ["unit cost", "reuse", "framework leverage"],
    verbs: ["reuse the pattern", "drop the cost"],
    monitored: false, metric: "partial (cost-per-interface tracking)",
    phase: "upon", audience: "customer" },

  { id: "B-48", cat: "IT", name: "Technical-debt reduction",
    meaning: "Old point-to-point integrations, undocumented scripts, legacy file drops — replaced by governed flows.",
    keywords: ["tech debt", "deprecation", "modernization"],
    verbs: ["deprecate the script", "retire the integration"],
    monitored: false, metric: "partial (decommission counts)",
    phase: "both", audience: "customer",
    topNonMonitored: 8 },

  { id: "B-49", cat: "IT", name: "Observability & root-cause speed",
    meaning: "Every business transaction can be traced end-to-end — RCA goes from days to minutes.",
    keywords: ["observability", "tracing", "correlation ID", "RCA"],
    verbs: ["trace the transaction", "root-cause the issue"],
    monitored: true, metric: "MTTR + trace-completeness", target: "100% of P1 incidents",
    phase: "during", audience: "both" },

  // ─── 09 · Customer Experience ────────────────────────────────────────
  { id: "B-50", cat: "CX", name: "Customer order visibility",
    meaning: "Customers see real-time order status — production stage, expected ship date, exceptions.",
    keywords: ["order status", "customer portal", "transparency"],
    verbs: ["surface the status", "notify the customer"],
    monitored: true, metric: "% of orders with live status", target: "100%",
    phase: "upon", audience: "customer" },

  { id: "B-51", cat: "CX", name: "Delivery reliability",
    meaning: "When the system says it'll ship Thursday, it ships Thursday — every time.",
    keywords: ["reliability", "dependability", "customer trust"],
    verbs: ["commit the date", "keep the promise"],
    monitored: true, metric: "promise-keep rate", target: "≥ 97%",
    phase: "upon", audience: "customer" },

  { id: "B-52", cat: "CX", name: "Quality consistency",
    meaning: "Customers stop seeing batch-to-batch variation in product characteristics.",
    keywords: ["consistency", "Cpk", "variation"],
    verbs: ["control the variation", "sustain the spec"],
    monitored: true, metric: "Cpk/Ppk", target: "≥ 1.33 critical characteristics",
    phase: "upon", audience: "both" },

  { id: "B-53", cat: "CX", name: "Configurability & mass-customization capability",
    meaning: "Engineering-to-order or configure-to-order variants are handled by the system — not by heroics.",
    keywords: ["CTO", "ETO", "configure", "variants"],
    verbs: ["configure the variant", "validate the configuration"],
    monitored: true, metric: "% orders auto-configured", target: "≥ 80%",
    phase: "upon", audience: "customer" },

  // ─── 10 · Sustainability & ESG ───────────────────────────────────────
  { id: "B-54", cat: "ESG", name: "Energy per unit produced",
    meaning: "kWh consumed per part — measured per line/SKU/shift, with attribution to root causes.",
    keywords: ["energy intensity", "kWh/unit", "ESG", "Scope 1+2"],
    verbs: ["meter the energy", "attribute the consumption"],
    monitored: true, metric: "kWh/unit", target: "-10% typical",
    phase: "upon", audience: "customer",
    topMonitored: 13 },

  { id: "B-55", cat: "ESG", name: "Scrap & waste reduction",
    meaning: "Material that's discarded — by weight, value, or category — falls as quality and yield improve.",
    keywords: ["scrap", "waste", "lean", "yield"],
    verbs: ["reduce the scrap", "prevent the waste"],
    monitored: true, metric: "scrap rate %", target: "-30%",
    phase: "upon", audience: "both",
    topMonitored: 14 },

  { id: "B-56", cat: "ESG", name: "Carbon-aware reporting (Scope 1, 2, partial 3)",
    meaning: "Audit-ready ESG metrics tied to actual production data, not estimates.",
    keywords: ["carbon", "Scope 1/2/3", "CSRD", "ESG reporting"],
    verbs: ["report the emissions", "attribute the carbon"],
    monitored: true, metric: "tCO₂e per period",
    phase: "upon", audience: "customer" },

  { id: "B-57", cat: "ESG", name: "Materials circularity & recycled content tracking",
    meaning: "Track recycled-input share, recoverable-output share — proven, not claimed.",
    keywords: ["circularity", "recycled content", "recovery"],
    verbs: ["trace the recycled content", "certify the input"],
    monitored: true, metric: "recycled-content %",
    phase: "upon", audience: "customer" },

  // ─── 11 · Risk & Resilience ──────────────────────────────────────────
  { id: "B-58", cat: "RISK", name: "Recall scope & speed",
    meaning: "When a recall is needed, scope is precise (exact lots affected) and execution is fast.",
    keywords: ["recall", "recall scope", "containment"],
    verbs: ["scope the recall", "contain the lots", "notify the customers"],
    monitored: true, metric: "hours to scope recall", target: "≤ 4h",
    phase: "upon", audience: "customer" },

  { id: "B-59", cat: "RISK", name: "Single-point-of-failure reduction",
    meaning: "No critical flow depends on one machine, one person, one script, one server.",
    keywords: ["SPOF", "resilience", "redundancy"],
    verbs: ["eliminate the SPOF", "build the redundancy"],
    monitored: false, metric: "partial (SPOF audit count)",
    phase: "upon", audience: "customer",
    topNonMonitored: 9 },

  { id: "B-60", cat: "RISK", name: "Cyber-risk surface reduction",
    meaning: "Consolidating integrations, retiring old protocols, enforcing identity everywhere — shrinks the attack surface.",
    keywords: ["cybersecurity", "attack surface", "zero trust"],
    verbs: ["shrink the surface", "enforce the identity"],
    monitored: true, metric: "vuln count, MTTR for security incidents",
    phase: "both", audience: "customer" },

  { id: "B-61", cat: "RISK", name: "Regulatory-risk reduction",
    meaning: "Probability of a regulatory finding or fine drops because audit trails, validations, and signatures are built into the flows.",
    keywords: ["regulatory", "FDA", "GMP", "21 CFR", "ICH"],
    verbs: ["satisfy the regulator", "evidence the compliance"],
    monitored: true, metric: "findings count", target: "0 critical",
    phase: "upon", audience: "customer" },

  { id: "B-62", cat: "RISK", name: "Supply-disruption resilience",
    meaning: "When a supplier goes down or a plant has a fire, you can see exposure, find alternates, and re-plan fast.",
    keywords: ["supply chain risk", "disruption", "alternate sourcing"],
    verbs: ["assess the exposure", "re-source the part"],
    monitored: false, metric: "felt only when disruption hits",
    phase: "upon", audience: "customer",
    topNonMonitored: 10 },

  // ─── 12 · Strategic & Competitive ────────────────────────────────────
  { id: "B-63", cat: "STRAT", name: "Time-to-market for new products",
    meaning: "From product concept to first saleable unit — compressed because design, planning, and ramp-up are no longer serial bottlenecks.",
    keywords: ["TTM", "NPI", "new product introduction"],
    verbs: ["launch the product", "ramp the volume"],
    monitored: true, metric: "months from concept to first ship", target: "-25%",
    phase: "upon", audience: "customer",
    topMonitored: 15 },

  { id: "B-64", cat: "STRAT", name: "M&A integration capability",
    meaning: "Acquired plants and businesses can be plugged into the mesh in weeks instead of years.",
    keywords: ["M&A", "post-merger integration", "PMI"],
    verbs: ["integrate the acquisition", "onboard the plant"],
    monitored: true, metric: "months to integrate", target: "≤ 6 months for typical acquisition",
    phase: "upon", audience: "customer" },

  { id: "B-65", cat: "STRAT", name: "Servitization readiness",
    meaning: "Capability to sell outcomes (uptime, throughput, performance) rather than just products.",
    keywords: ["servitization", "outcome-based", "as-a-service"],
    verbs: ["sell the outcome", "price the uptime"],
    monitored: false, metric: "once you have a service line",
    phase: "upon", audience: "customer" },

  { id: "B-66", cat: "STRAT", name: "Brand & reputation lift",
    meaning: "Customers and analysts see the company as a credible, modern, reliable manufacturer.",
    keywords: ["brand", "reputation", "market perception"],
    verbs: ["build the reputation", "win the customer"],
    monitored: false, metric: "surveys, NPS, win-rate trends are proxies",
    phase: "upon", audience: "customer",
    topNonMonitored: 11 },

  { id: "B-67", cat: "STRAT", name: "Talent attraction & retention",
    meaning: "Engineers and operators prefer to work somewhere with modern tools — and stay longer.",
    keywords: ["employer brand", "talent", "retention"],
    verbs: ["attract the talent", "retain the engineer"],
    monitored: false, metric: "partial (retention rate, time-to-fill)",
    phase: "upon", audience: "both" },

  // ─── 13 · Cultural & Behavioral ──────────────────────────────────────
  { id: "B-68", cat: "CULT", name: "Cross-functional collaboration",
    meaning: "Engineering, manufacturing, quality, finance, and IT actually work from the same data and the same vocabulary.",
    keywords: ["collaboration", "silos", "alignment"],
    verbs: ["align the teams", "share the source"],
    monitored: false, metric: "felt as fewer status meetings, fewer escalations",
    phase: "upon", audience: "both",
    topNonMonitored: 12 },

  { id: "B-69", cat: "CULT", name: "Data-driven decision culture",
    meaning: "'What does the data say?' replaces 'what does the senior person say?'",
    keywords: ["data culture", "evidence-based", "analytics adoption"],
    verbs: ["ask the data", "question the assumption"],
    monitored: false, metric: "qualitative",
    phase: "upon", audience: "both",
    topNonMonitored: 13 },

  { id: "B-70", cat: "CULT", name: "Continuous-improvement habit",
    meaning: "Lean/Six Sigma/Kaizen efforts have rich data to work with, so they actually compound rather than reset.",
    keywords: ["Kaizen", "CI", "lean", "Six Sigma"],
    verbs: ["improve the process", "sustain the gain"],
    monitored: false, metric: "partial (CI events, savings tracked)",
    phase: "upon", audience: "both" },

  { id: "B-71", cat: "CULT", name: "Trust in the numbers",
    meaning: "The single most undersold benefit: when leadership and the floor both believe the dashboard, conversations change entirely.",
    keywords: ["trust", "credibility", "single source of truth"],
    verbs: ["trust the dashboard", "cite the source"],
    monitored: false, metric: "the most undersold benefit of all",
    phase: "upon", audience: "both",
    topNonMonitored: 14 },

  { id: "B-72", cat: "CULT", name: "Reduced firefighting culture",
    meaning: "Heroes go from being the people who put out fires to the people who prevent them.",
    keywords: ["firefighting", "proactive", "prevention"],
    verbs: ["prevent the fire", "plan the work"],
    monitored: false, metric: "qualitative",
    phase: "upon", audience: "both",
    topNonMonitored: 15 },

  // ─── 14 · Knowledge & Continuous Improvement ─────────────────────────
  { id: "B-73", cat: "KM", name: "Lessons-learned propagation across plants",
    meaning: "A fix found in Plant A spreads to Plants B–E in days — instead of being rediscovered painfully at each site.",
    keywords: ["lessons learned", "best practice", "replication"],
    verbs: ["replicate the fix", "propagate the learning"],
    monitored: false, metric: "partial (replication-cycle time)",
    phase: "upon", audience: "customer" },

  { id: "B-74", cat: "KM", name: "Best-practice library",
    meaning: "Runbooks, BoP templates, NCR-resolution patterns, and KPI definitions live in one curated, searchable place.",
    keywords: ["runbook", "knowledge base", "templates"],
    verbs: ["document the playbook", "search the runbook"],
    monitored: false, metric: "usage analytics exist",
    phase: "upon", audience: "both" },

  { id: "B-75", cat: "KM", name: "Faster new-hire ramp at every level",
    meaning: "Whether on the floor, in engineering, or in IT — new hires can be productive in weeks because the system has the context.",
    keywords: ["onboarding", "ramp", "productivity"],
    verbs: ["ramp the hire", "transfer the context"],
    monitored: true, metric: "time-to-productivity", target: "-40% vs baseline",
    phase: "upon", audience: "both" },

  { id: "B-76", cat: "KM", name: "Audit-driven improvement loop",
    meaning: "Every audit finding (internal or external) feeds back into the runbook, the BoP, or the design.",
    keywords: ["audit", "finding", "CAPA", "learning"],
    verbs: ["close the finding", "improve the standard"],
    monitored: true, metric: "audit-finding closure time", target: "≤ 30 days",
    phase: "upon", audience: "both" },
];

// Convenience selectors
export const benefitsByCategory = (catId) => BENEFITS.filter(b => b.cat === catId);
export const monitoredBenefits  = () => BENEFITS.filter(b => b.monitored);
export const feltBenefits       = () => BENEFITS.filter(b => !b.monitored);
export const topMonitored       = () => BENEFITS.filter(b => b.topMonitored).sort((a, b) => a.topMonitored - b.topMonitored);
export const topNonMonitored    = () => BENEFITS.filter(b => b.topNonMonitored).sort((a, b) => a.topNonMonitored - b.topNonMonitored);
export const benefitById        = (id) => BENEFITS.find(b => b.id === id);

export const PHASES = [
  { id: "during", label: "Visible during build (Phase 0–2)",  color: "#00e5ff" },
  { id: "upon",   label: "Realized upon steady-state (Phase 3+)", color: "#00ff88" },
  { id: "both",   label: "Grows continuously",               color: "#ffb300" },
];

export const AUDIENCES = [
  { id: "customer", label: "Customer (sponsor lens)",  color: "#ffd600" },
  { id: "adopter",  label: "Adopter (operator lens)",  color: "#c084fc" },
  { id: "both",     label: "Both equally",            color: "#00e5ff" },
];
